'use client';

import { useMemo, useState } from 'react';
import { UserRoundPlus } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { DatePicker } from '@/components/ui/date-picker';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { AddContactDialog } from '@/components/contacts/add-contact-dialog';
import { useMatterContacts } from '@/lib/services/contacts/hooks';
import { getContactDisplayName } from '@/lib/services/contacts/utils';
import { fromIsoDateOnly, toIsoDateOnly } from '@/lib/formatters/date-only';
import type {
  Handling,
  SetHandlingRequest
} from '@/lib/services/matter-handling';

/** The note cap matches the backend's `@MaxLength(2000)` on `handlingNote`. */
const NOTE_MAX_LENGTH = 2000;

export interface HandlingDialogProps {
  matterId: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Current handling, when editing an already-handed-over row. */
  handling?: Handling | null;
  /** What is being handed over, e.g. "ANZ" or "3 institutions". */
  subjectLabel: string;
  isPending?: boolean;
  onSubmit: (data: SetHandlingRequest, handlerName?: string) => void;
}

/**
 * The one place handling is set or edited (EH-004, EH-010).
 *
 * The handler is always a contact **on this matter** — a `contact_matters` row,
 * never free text and never a contact from another matter (EH-DR-001). That is why
 * this picks from the matter's own contacts and offers to create one rather than
 * accepting a typed name: a typed name could not be pointed at by the FK, so the
 * handler-removal guards VALE-837 built would have nothing to protect.
 *
 * Taking work back into Vale is deliberately NOT here — it is a one-click action on
 * the row itself (EH-005), because burying an undo inside a dialog is the asymmetry
 * this feature is supposed to avoid.
 */
export function HandlingDialog({
  matterId,
  open,
  onOpenChange,
  handling,
  subjectLabel,
  isPending = false,
  onSubmit
}: HandlingDialogProps) {
  const [addContactOpen, setAddContactOpen] = useState(false);
  /*
    **A contact created from inside this dialog becomes the handler** (VALE-1760).

    `AddContactDialog` used to be mounted here with no success callback at all,
    so creating someone dropped the reader back on an unchanged form and left
    them to find the new name in the dropdown. Creating a contact *here* has no
    other purpose than handing the work to them, so adopting it is the whole
    point of the affordance.

    Held on this component rather than inside the form, so it survives whatever
    the create panel does to the layer below it.
  */
  const [presetContactMatterId, setPresetContactMatterId] = useState<
    number | null
  >(null);
  const isEditing = !!handling?.handledBy;

  return (
    <>
      <Dialog
        open={open}
        /*
          **This dialog does not close because the create panel opened on top of
          it.** `AddContactDialog` is portalled as a *sibling* layer (see below),
          so Radix registers it above this one and its opening pointer-down reads
          as an interaction outside — the default response to which is to dismiss
          this layer. That is what made "Add a new contact" look broken from the
          Inventory row and the item drawer alike: the handover form vanished
          under the create panel, and with it any chance of adopting the contact
          it just created (VALE-1760).

          Guarding the close here rather than with `onInteractOutside` on the
          content covers every route Radix can take to `false` — outside press,
          focus leaving, and the Escape that the create panel's own handler lets
          bubble — with one condition instead of three handlers to keep in step.
        */
        onOpenChange={(next) => {
          if (!next && addContactOpen) return;
          if (!next) setPresetContactMatterId(null);
          onOpenChange(next);
        }}
      >
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {isEditing ? 'Update handling' : 'Hand over to someone else'}
            </DialogTitle>
            <DialogDescription>
              Record that someone outside the firm is dealing with{' '}
              {subjectLabel}. Vale stops chasing it and stops writing to it, and
              it no longer counts as the firm&apos;s outstanding work.
            </DialogDescription>
          </DialogHeader>

          {/*
            Mounted only while open, so every field's `useState` initialiser re-runs
            on reopen. That is the re-seed a `useEffect` used to do — reopening after
            a cancel must show what is stored, not the abandoned edit — without
            calling setState synchronously in an effect, which cascades renders.
          */}
          {open && (
            <HandlingDialogForm
              matterId={matterId}
              handling={handling}
              isEditing={isEditing}
              isPending={isPending}
              onCancel={() => onOpenChange(false)}
              presetContactMatterId={presetContactMatterId}
              onAddContact={() => setAddContactOpen(true)}
              onSubmit={onSubmit}
            />
          )}
        </DialogContent>
      </Dialog>

      {/*
        The inline "create a new contact" path (EH-004). Rendered as a sibling
        rather than nested inside the dialog above: two stacked Radix modals fight
        over the body scroll lock, which is the LF-6365 shape.
      */}
      <AddContactDialog
        matterId={matterId}
        open={addContactOpen}
        onOpenChange={setAddContactOpen}
        onSuccess={(created) => {
          // `contactMatterId` is what the picker is keyed on — a bare `contact.id`
          // would never match an option. A contact created outside a matter has
          // none, and then there is simply nothing to preselect.
          if (created?.contactMatterId != null) {
            setPresetContactMatterId(created.contactMatterId);
          }
        }}
      />
    </>
  );
}

function HandlingDialogForm({
  matterId,
  handling,
  isEditing,
  isPending,
  presetContactMatterId,
  onCancel,
  onAddContact,
  onSubmit
}: {
  matterId: number;
  handling?: Handling | null;
  isEditing: boolean;
  isPending: boolean;
  /**
   * A contact just created from inside this dialog, to adopt as the selection.
   * Seeds the initial value on mount and is also adopted mid-life, because this
   * form stays mounted while the create panel is open — see the adjustment
   * below.
   */
  presetContactMatterId: number | null;
  onCancel: () => void;
  onAddContact: () => void;
  onSubmit: (data: SetHandlingRequest, handlerName?: string) => void;
}) {
  const { data, isLoading } = useMatterContacts(matterId);

  // Lazy initialisers: seeded once per mount, and this only mounts while open.
  const [contactMatterId, setContactMatterId] = useState<number | null>(
    () => presetContactMatterId ?? handling?.handledBy?.contactMatterId ?? null
  );
  const [note, setNote] = useState(() => handling?.handlingNote ?? '');
  const [expectedBy, setExpectedBy] = useState<Date | undefined>(() =>
    fromIsoDateOnly(handling?.handlingExpectedBy)
  );

  /*
    **Adopt a contact created while this form was open**, during render rather
    than in an effect. This is React's documented way to adjust state when a
    prop changes: the re-render happens before the browser paints, so nobody
    ever sees the stale selection, and there is no second commit the way a
    `useEffect` would give.

    Remounting on the preset instead (a `key`) would be simpler and wrong — it
    would throw away the note and the expected-by date the reader had already
    typed before they went off to create the handler.
  */
  const [adoptedPreset, setAdoptedPreset] = useState(presetContactMatterId);
  if (presetContactMatterId !== adoptedPreset) {
    setAdoptedPreset(presetContactMatterId);
    if (presetContactMatterId !== null) {
      setContactMatterId(presetContactMatterId);
    }
  }

  /**
   * Only contacts that actually have a `contactMatterId` can be handlers — the
   * field is what the FK points at. A contact without one cannot be selected
   * rather than being offered and failing on save.
   */
  const candidates = useMemo(
    () =>
      (data?.contacts ?? []).filter(
        (contact): contact is typeof contact & { contactMatterId: number } =>
          typeof contact.contactMatterId === 'number'
      ),
    [data?.contacts]
  );

  const selected = candidates.find(
    (c) => c.contactMatterId === contactMatterId
  );

  const handleSubmit = () => {
    if (contactMatterId === null) return;
    onSubmit(
      {
        handledByContactMatterId: contactMatterId,
        handlingNote: note.trim() || null,
        handlingExpectedBy: expectedBy ? toIsoDateOnly(expectedBy) : null
      },
      selected ? getContactDisplayName(selected) : undefined
    );
  };

  return (
    <>
      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="handling-handler">Who is handling it</Label>
          <Select
            value={contactMatterId === null ? '' : String(contactMatterId)}
            onValueChange={(value) => setContactMatterId(Number(value))}
            disabled={isLoading || isPending}
          >
            <SelectTrigger
              id="handling-handler"
              data-testid="handling-handler-select"
            >
              <SelectValue
                placeholder={
                  isLoading
                    ? 'Loading contacts…'
                    : 'Choose a contact on this matter'
                }
              />
            </SelectTrigger>
            <SelectContent>
              {candidates.map((contact) => (
                <SelectItem
                  key={contact.contactMatterId}
                  value={String(contact.contactMatterId)}
                >
                  {getContactDisplayName(contact)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            size="xs"
            density="compact"
            gap="standard"
            type="button"
            variant="ghost"
            onClick={onAddContact}
            disabled={isPending}
          >
            <UserRoundPlus className="h-3.5 w-3.5" />
            Add a new contact
          </Button>
        </div>

        <div className="space-y-2">
          <Label htmlFor="handling-note">
            What is being done{' '}
            <span className="text-muted-foreground font-normal">
              (optional)
            </span>
          </Label>
          <Textarea
            id="handling-note"
            data-testid="handling-note"
            value={note}
            maxLength={NOTE_MAX_LENGTH}
            onChange={(event) => setNote(event.target.value)}
            placeholder="Closing the account directly; sale settles 14 March."
            disabled={isPending}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="handling-expected-by">
            Expected to be finished by{' '}
            <span className="text-muted-foreground font-normal">
              (optional)
            </span>
          </Label>
          {/*
            `clearable`, because this field is `(optional)` and placeholdered
            "No date set" — both promise an empty state, and without a clear
            control a date once set could only be swapped, never removed
            (VALE-1854).
          */}
          <DatePicker
            date={expectedBy}
            onDateChange={setExpectedBy}
            placeholder="No date set"
            disabled={isPending}
            clearable
          />
        </div>
      </div>

      <DialogFooter>
        <Button
          size="xs"
          density="compact"
          variant="outline"
          onClick={onCancel}
          disabled={isPending}
        >
          Cancel
        </Button>
        <Button
          size="xs"
          density="compact"
          onClick={handleSubmit}
          disabled={contactMatterId === null || isPending}
          data-testid="handling-submit"
        >
          {isEditing ? 'Save' : 'Hand over'}
        </Button>
      </DialogFooter>
    </>
  );
}
